import { BANK_ACCOUNT_KIND_LABELS, type CashPosition } from "@station/contracts";
import {
  Alert,
  Button,
  cn,
  DateInput,
  EmptyState,
  FormField,
  MoneyText,
  PageHeader,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeftRight, Wallet } from "lucide-react";
import { type ReactElement, useId } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { todayIstanbul } from "../../../features/accounting/shared.js";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../features/counterparties/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { useDocumentTitle } from "../../../lib/hooks.js";
import { queryKeys } from "../../../lib/query.js";
import { usePermissions } from "../../../lib/session.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

const searchSchema = z.object({
  asOf: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/_app/finans/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CashPositionPage,
});

/**
 * Günlük nakit pozisyonu — kiracının GÖRÜLEBİLEN tüm şirketlerini kapsar (şirket seçici yoktur; kırılım tabloda).
 * Bugün beklenen girişler (POS valörü, çek, DBS tahsilatı) ve çıkışlar (çek, kredi taksiti) tek ekranda toplanır.
 */
function CashPositionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const permissions = usePermissions();
  const shape = useTenantShape();
  const today = todayIstanbul();
  const asOf = Route.useSearch().asOf ?? today;
  useDocumentTitle(t("menu.cashPosition"));

  const position = useQuery({
    queryKey: queryKeys.treasury(null, "cash-position", { asOf }),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/treasury/reports/cash-position", { params: { query: { asOf } } })).data!,
  });

  return (
    <>
      <PageHeader
        title={t("menu.cashPosition")}
        description={t("treasury.position.description")}
        breadcrumb={[{ label: t("menu.groups.finance") }, { label: t("menu.cashPosition") }]}
        actions={
          <>
            <div className="flex items-end gap-2 print:hidden">
              <FormField label={t("treasury.position.asOf")} className="w-44">
                {(control) => (
                  <DateInput
                    {...control}
                    value={asOf}
                    max={today}
                    onValueChange={(value) =>
                      void navigate({ search: { asOf: value ?? undefined }, replace: true })
                    }
                  />
                )}
              </FormField>
            </div>
            {permissions.has("treasury.txn.create") ? (
              <Button asChild>
                <Link to="/finans/hareket-yeni">
                  <ArrowLeftRight aria-hidden="true" />
                  {t("treasury.txn.new")}
                </Link>
              </Button>
            ) : null}
          </>
        }
      />
      {position.error ? (
        <Alert tone="danger">{errorMessage(position.error)}</Alert>
      ) : position.isPending ? (
        <SkeletonRows rows={8} />
      ) : position.data.companies.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<Wallet />}
          title={t("treasury.position.emptyTitle")}
          description={t("treasury.position.emptyDescription")}
        />
      ) : (
        <div className="grid gap-6">
          <TotalsGrid totals={position.data.totals} heading={t("treasury.position.totalsHeading")} />
          {position.data.companies.map((company) => (
            <CompanySection
              key={company.companyId}
              company={company}
              single={shape.isSingleCompany || position.data.companies.length === 1}
              singleStation={shape.isSingleStation}
            />
          ))}
        </div>
      )}
    </>
  );
}

type Totals = CashPosition["totals"];

const TOTAL_KEYS = [
  "cash",
  "bank",
  "bankAvailable",
  "posPending",
  "posDueToday",
  "chequesDueToday",
  "chequesPayableToday",
  "dbsDueToday",
  "loanDueToday",
] as const;

function TotalsGrid({ totals, heading }: { totals: Totals; heading: string }): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="grid gap-3">
      <h2 id={headingId} className="text-base font-semibold">
        {heading}
      </h2>
      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {TOTAL_KEYS.map((key) => (
          <div
            key={key}
            className="rounded-lg border bg-card text-card-foreground shadow-card grid gap-1 p-4"
          >
            <dt className="text-sm text-muted-foreground">{t(`treasury.position.totals.${key}`)}</dt>
            <dd className="text-lg font-semibold">
              <MoneyText value={totals[key]} colorize />
            </dd>
          </div>
        ))}
        <div className="rounded-lg border bg-card text-card-foreground shadow-card grid gap-1 border-primary/40 bg-primary/5 p-4">
          <dt className="text-sm font-medium">{t("treasury.position.totals.netToday")}</dt>
          <dd className="text-lg font-semibold">
            <MoneyText value={totals.netToday} colorize />
          </dd>
          <dd className="text-xs text-muted-foreground">{t("treasury.position.netHint")}</dd>
        </div>
      </dl>
    </section>
  );
}

function CompanySection({
  company,
  single,
  singleStation,
}: {
  company: CashPosition["companies"][number];
  single: boolean;
  singleStation: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="grid gap-3">
      {/* Tek şirketli kiracıda şirket adı başlık olarak tekrar edilmez; yalnızca kırılım gösterilir. */}
      <h2 id={headingId} className={cn("text-base font-semibold", single && "sr-only")}>
        {single ? t("treasury.position.breakdownHeading") : company.companyName}
      </h2>
      <div className="grid gap-4 lg:grid-cols-2">
        <SimpleTable
          caption={t("treasury.position.cashRegisters")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("treasury.position.cashRegisters")}
              </th>
              <th scope="col" className={thClass}>
                {t("treasury.fields.station")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("treasury.fields.balance")}
              </th>
            </tr>
          }
          foot={
            <tr>
              <td className={tdClass} colSpan={2}>
                {t("common.total")}
              </td>
              <td className={tdRightClass}>
                <MoneyText value={company.totals.cash} />
              </td>
            </tr>
          }
        >
          {company.cashRegisters.length === 0 ? (
            <tr className="border-t">
              <td className={tdClass} colSpan={3}>
                {t("treasury.position.noCashRegisters")}
              </td>
            </tr>
          ) : (
            company.cashRegisters.map((register) => (
              <tr key={register.id} className="border-t">
                <td className={tdClass}>
                  <Link
                    to="/finans/kasalar/$id"
                    params={{ id: register.id }}
                    className="font-medium text-primary underline-offset-4 hover:underline"
                  >
                    {register.name}
                  </Link>
                </td>
                <td className={tdClass}>{register.stationName ?? t("treasury.fields.headOffice")}</td>
                <td className={tdRightClass}>
                  <MoneyText value={register.balance} colorize />
                </td>
              </tr>
            ))
          )}
        </SimpleTable>

        <SimpleTable
          caption={t("treasury.position.bankAccounts")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("treasury.position.bankAccounts")}
              </th>
              <th scope="col" className={thClass}>
                {t("treasury.fields.kind")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("treasury.fields.balance")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("treasury.fields.availableBalance")}
              </th>
            </tr>
          }
          foot={
            <tr>
              <td className={tdClass} colSpan={2}>
                {t("common.total")}
              </td>
              <td className={tdRightClass}>
                <MoneyText value={company.totals.bank} />
              </td>
              <td className={tdRightClass}>
                <MoneyText value={company.totals.bankAvailable} />
              </td>
            </tr>
          }
        >
          {company.bankAccounts.length === 0 ? (
            <tr className="border-t">
              <td className={tdClass} colSpan={4}>
                {t("treasury.position.noBankAccounts")}
              </td>
            </tr>
          ) : (
            company.bankAccounts.map((account) => (
              <tr key={account.id} className="border-t">
                <td className={tdClass}>
                  <Link
                    to="/finans/bankalar/$id"
                    params={{ id: account.id }}
                    className="font-medium text-primary underline-offset-4 hover:underline"
                  >
                    {account.name}
                  </Link>
                  <br />
                  <span className="text-xs text-muted-foreground">{account.bankName}</span>
                </td>
                <td className={tdClass}>{BANK_ACCOUNT_KIND_LABELS[account.kind]}</td>
                <td className={tdRightClass}>
                  <MoneyText value={account.balance} colorize />
                </td>
                <td className={tdRightClass}>
                  <MoneyText value={account.availableBalance} colorize />
                  {account.overdraftLimit !== "0" ? (
                    <>
                      <br />
                      <span className="text-xs text-muted-foreground">
                        {t("treasury.position.overdraftIncluded")}
                      </span>
                    </>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </SimpleTable>

        <SimpleTable
          caption={t("treasury.position.posPending")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("treasury.fields.terminal")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("treasury.position.pendingNet")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("treasury.position.dueToday")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("treasury.position.overdue")}
              </th>
            </tr>
          }
        >
          {company.posPending.length === 0 ? (
            <tr className="border-t">
              <td className={tdClass} colSpan={4}>
                {t("treasury.position.noPos")}
              </td>
            </tr>
          ) : (
            company.posPending.map((terminal) => (
              <tr key={terminal.terminalId} className="border-t">
                <td className={tdClass}>
                  <span className="font-medium">{terminal.terminalName}</span>
                  {singleStation ? null : (
                    <>
                      <br />
                      <span className="text-xs text-muted-foreground">{terminal.stationName}</span>
                    </>
                  )}
                </td>
                <td className={tdRightClass}>
                  <MoneyText value={terminal.pendingNet} />
                </td>
                <td className={tdRightClass}>
                  <MoneyText value={terminal.dueToday} />
                </td>
                <td className={tdRightClass}>
                  {terminal.overdue === "0" ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <StatusBadge tone="danger">{t("treasury.position.overdueBadge")}</StatusBadge>
                      <MoneyText value={terminal.overdue} className="font-medium text-destructive" />
                    </span>
                  )}
                </td>
              </tr>
            ))
          )}
        </SimpleTable>

        <SimpleTable
          caption={t("treasury.position.stations")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("treasury.position.stations")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("treasury.position.totals.cash")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("treasury.position.totals.posPending")}
              </th>
            </tr>
          }
        >
          {company.stations.length === 0 ? (
            <tr className="border-t">
              <td className={tdClass} colSpan={3}>
                {t("treasury.position.noStations")}
              </td>
            </tr>
          ) : (
            company.stations.map((station) => (
              <tr key={station.stationId ?? "head-office"} className="border-t">
                <td className={tdClass}>{station.stationName}</td>
                <td className={tdRightClass}>
                  <MoneyText value={station.cash} colorize />
                </td>
                <td className={tdRightClass}>
                  <MoneyText value={station.posPending} />
                </td>
              </tr>
            ))
          )}
        </SimpleTable>
      </div>
    </section>
  );
}
